import { ImageScaleView, type ViewOpts } from '@play-co/timestep-core/ui';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import { dreamDriftTheme } from '../dreamDrift.theme';

/** Visual statuses available for one Dream Drift progression node. */
export type DreamDriftNodeStatus = 'cleared' | 'current' | 'open';

/** Centered Dream Drift progression node displaying one themed state image. */
export default class DreamDriftNode extends ImageScaleView {
  /** Theme retained for the lifetime of this plate, including event rollover. */
  private readonly theme = dreamDriftTheme();

  /** Zero-based position of this node in the configured progression path. */
  public index = 0;

  /** Flag displayed above nodes that complete a progression checkpoint. */
  private readonly checkpointFlag: ImageScaleView;

  /** One-based progression number displayed on current and open nodes. */
  private readonly label: LangBitmapFontTextView;

  /**
   * Create an unconfigured progression node at its map position.
   *
   * @param opts Parent, position, scale, and other base view options.
   */
  constructor(opts: ViewOpts) {
    // Preserve source dimensions so the map can scale the complete node uniformly.
    super({
      ...opts,
      width: 173,
      height: 152,
      scaleMethod: 'contain',
      centerOnOrigin: true,
    });

    // Place the hidden checkpoint flag above the node's upper-right edge.
    this.checkpointFlag = new ImageScaleView({
      superview: this,
      image: this.theme.getMapImagePath('checkpoint_flag.png'),
      x: 173,
      y: 8,
      width: 68,
      height: 76,
      visible: false,
    });

    // Center the hidden number inside the node's upper status plate.
    this.label = new LangBitmapFontTextView({
      superview: this,
      x: 80,
      y: 55,
      width: 100,
      height: 55,
      font: 'Body',
      size: 70,
      align: 'center',
      verticalAlign: 'center',
      centerOnOrigin: true,
      visible: false,
      scaleY: 0.7,
    });
  }

  /**
   * Configure the node's progression position and visual status.
   *
   * @param opts Zero-based path index, status, and checkpoint designation.
   */
  public setup({
    index,
    isCheckpoint,
    status,
  }: {
    index: number;
    isCheckpoint: boolean;
    status: DreamDriftNodeStatus;
  }): void {
    // Retain the configured path position for node-specific interactions.
    this.index = index;

    // Fit the plate and its label to this theme's artwork.
    const { width, height, label, flag } =
      this.theme.getNodeStyle(isCheckpoint);
    this.updateOpts({
      width,
      height,
      centerOnOrigin: true,
    });

    // Keep the number aligned with the themed plate's perspective and color.
    this.label.updateOpts(label);

    // Show the flag only on nodes that complete a progression checkpoint.
    this.checkpointFlag.updateOpts({ ...flag, visible: isCheckpoint });

    // Resolve and display the themed image associated with the requested status.
    const image = this.theme.getNodeImage({ status, isCheckpoint });
    this.setImage(image);

    // Apply the explicit label behavior associated with every node status.
    switch (status) {
      case 'cleared':
        // Cleared nodes use only their checkmark artwork.
        this.label.hide();
        return;
      case 'current':
      case 'open':
        // Current and upcoming nodes share the theme's progression-number styling.
        break;
      default:
        status satisfies never;
    }

    // Current and upcoming nodes display their one-based progression number.
    this.label.localeText = () => `${index + 1}`;
    this.label.show();
  }
}
